import React from 'react';
import ReactDOM from 'react-dom';
import {ApolloClient} from 'apollo-client';
import {ApolloLink} from 'apollo-link';
import {HttpLink} from 'apollo-link-http';
import {onError} from 'apollo-link-error';

import AbacusClientUtils from '@homeaway/abacus-client-utils';
import {logExperiment} from '@homeaway/abacus-client-utils';
import {TrackFocus} from '@homeaway/a11y-tools';
import getCurrentScriptPath from '@homeaway/current-script-path';

import {createStore} from './store';
import Root from './components/Root/Root';
import haWrapper from './utils/haWrapper';
import logger from './utils/clientLogger';
import {sendCustomAnalytics} from './utils/loadTimeAnalyticsWrapper';
import {
    HA_SERP_DESKTOP_FILTER_BTN,
    HA_SERP_EXPANDED_MOBILE_SEARCH_FIELDS,
    HA_SERP_MOBILE_HIT_HEADLINE,
    HA_SERP_NUMBER_OF_HITS,
    HA_SRS_SERIAL_PERCENT_BOOKED,
    HA_SERP_THREE_PACK
} from '../universal/abacus/experiments';
import {createCache} from './graphql/cache';

// Let webpack know where to load files from in production envs.
// This is primarily for code-splitting scenarios and for images.
if (!module.hot) {
    __webpack_public_path__ = getCurrentScriptPath(); // eslint-disable-line
}

/**
 * Main client-side entry point. This is not used during
 * server-side rendering, only after the javascript bundle
 * has been loaded into the browser.
 */

const trackFocus = new TrackFocus();

// Initial state as set by the server-side render
const initialState = window.__INITIAL_STATE__;
const initialApolloState = window.__INITIAL_APOLLO_STATE__;

// Initialize redux store
const store = createStore({initialState});

// initialize graphql client
const loggerLink = new ApolloLink((operation, forward) => {
    logger.captureBreadcrumb({
        message: `starting request for ${operation.operationName}`,
        category: 'graphQL',
        data: {
            name: operation.operationName,
            variables: operation.variables
        }
    });
    return forward(operation).map((data) => {
        logger.captureBreadcrumb({
            message: `ending request for ${operation.operationName}`,
            category: 'graphQL',
            data: {
                name: operation.operationName,
                variables: operation.variables
            }
        });
        return data;
    });
});

const errorLink = onError(({graphQLErrors, networkError}) => {
    if (graphQLErrors) {
        graphQLErrors.map(({message, locations, path}) => {
            logger.captureException(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
                {
                    fingerprint: ['Type: GraphQLError', `Message: ${message}`]
                }
            );
        });
    }
    if (networkError) {
        logger.captureException(`[Network error]: ${networkError}`, {
            fingerprint: ['Type: NetworkError', `Message: ${networkError}`]
        });
    }
});

const productionHeaders = {
    'x-csrf-token': haWrapper.crumb,
    'x-homeaway-site': store.getState().system.siteName
};

const nonProdHeaders = {
    'x-csrf-token': haWrapper.crumb
};

const client = new ApolloClient({
    link: ApolloLink.from([
        errorLink,
        loggerLink,
        new HttpLink({
            uri: '/serp/g',
            credentials: 'same-origin',
            headers:
                process.env.NODE_ENV !== 'production'
                    ? productionHeaders
                    : nonProdHeaders
        })
    ]),
    cache: createCache().restore(initialApolloState)
});

// set up abacus experiment cache
AbacusClientUtils.initialize(store.getState().abacus);
// log server-side evaluated ssr test
const {abacus} = store.getState();

const mobileHitTest = abacus[HA_SERP_MOBILE_HIT_HEADLINE];
if (mobileHitTest) {
    logExperiment(mobileHitTest);
}

const numberOfHits = abacus[HA_SERP_NUMBER_OF_HITS];
if (numberOfHits) {
    logExperiment(numberOfHits);
}

const srsSerialPercentBooked = abacus[HA_SRS_SERIAL_PERCENT_BOOKED];
if (srsSerialPercentBooked) {
    logExperiment(srsSerialPercentBooked);
}

const expandedMobileSearchFields =
    abacus[HA_SERP_EXPANDED_MOBILE_SEARCH_FIELDS];
const {arrival, departure} = store.getState().searchParams;
const isDateless = !arrival && !departure;
if (expandedMobileSearchFields && isDateless) {
    logExperiment(expandedMobileSearchFields);
}

const desktopMoreFilterBtnTst = abacus[HA_SERP_DESKTOP_FILTER_BTN];
if (desktopMoreFilterBtnTst) {
    logExperiment(desktopMoreFilterBtnTst);
}

const threePackTest = abacus[HA_SERP_THREE_PACK];
if (threePackTest) {
    logExperiment(threePackTest);
}

haWrapper.userTiming.measure('navigationStart');

// measure full page load
window.addEventListener('load', () => {
    const pageload = 'pageload';

    haWrapper.userTiming.measure(pageload);

    sendCustomAnalytics(pageload, 'measure', (analytics) => ({
        [pageload]: analytics.duration
    }));
});

// set up keyboard focus tracking
trackFocus.bindEvents();

ReactDOM.hydrate(
    <Root store={store} client={client} abacus={abacus} />,
    document.getElementById('root')
);
